import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Receipt, Download, FileSpreadsheet, FileText, Car, Loader2 } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const StaffReceipts = ({ caseId }) => {
  const [data, setData] = useState(null);
  const [year, setYear] = useState(null);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    api.get(`/staff/cases/${caseId}/receipts`, { params: year ? { year } : {} }).then((r) => setData(r.data)).catch(() => {});
  }, [caseId, year]);

  if (!data || (!data.receipts.length && !data.summary.years.length && !(data.trips || []).length)) return null;
  const s = data.summary;

  const exportReport = async (fmt) => {
    setExporting(fmt);
    try {
      const { data: blob } = await api.get(`/staff/cases/${caseId}/expense-report.${fmt}`, { params: year ? { year } : {}, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `expense-report-${year || "all"}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setExporting(null); }
  };

  const view = async (r) => {
    const { data: blob } = await api.get(`/files/${r.storage_path}`, { responseType: "blob" });
    window.open(URL.createObjectURL(blob), "_blank");
  };

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="ws-receipts">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[#114A31]"><Receipt className="h-5 w-5" /><h2 className="font-heading font-semibold text-[#1C1C1C]">Client receipts (Vault)</h2></div>
        <div className="flex gap-1 bg-[#F4F5F3] rounded-full p-1">
          {[null, ...s.years].map((y) => (
            <button key={y ?? "all"} onClick={() => setYear(y)} data-testid={`ws-receipts-year-${y ?? "all"}`}
              className={`text-xs font-semibold px-3 py-1 rounded-full ${year === y ? "bg-white text-[#114A31] shadow-sm" : "text-[#595D5A]"}`}>{y ?? "All"}</button>
          ))}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#F9F9F8] rounded-xl p-3"><p className="text-xs text-[#595D5A]">Total</p><p className="font-heading text-xl font-bold text-[#1C1C1C]" data-testid="ws-receipts-total">{money(s.total)}</p></div>
        <div className="bg-[#F9F9F8] rounded-xl p-3"><p className="text-xs text-[#595D5A]">Mileage · {s.mileage?.miles || 0} mi</p><p className="font-heading text-xl font-bold text-[#1C1C1C]" data-testid="ws-mileage-total">{money(s.mileage?.deduction)}</p></div>
        <div className="bg-[#E6F0EB] rounded-xl p-3 col-span-2"><p className="text-xs text-[#114A31]">Total deductible · {s.count} receipts, {s.mileage?.count || 0} trips</p>
          <p className="font-heading text-xl font-bold text-[#114A31]" data-testid="ws-grand-total">{money(s.grand_total)}</p>
          <p className="text-[11px] text-[#595D5A] truncate">{s.by_category.slice(0, 3).map((c) => `${c.category} ${money(c.amount)}`).join(" · ") || "—"}</p></div>
      </div>
      <div className="mt-3 flex gap-2 flex-wrap">
        <Button onClick={() => exportReport("pdf")} disabled={!!exporting} size="sm" data-testid="ws-export-pdf" className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white">
          {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4 mr-1.5" /> Expense report PDF</>}
        </Button>
        <Button onClick={() => exportReport("csv")} disabled={!!exporting} size="sm" variant="outline" data-testid="ws-export-csv" className="rounded-full border-[#E5E5E5] hover:border-[#114A31]">
          {exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileSpreadsheet className="h-4 w-4 mr-1.5" /> CSV</>}
        </Button>
      </div>
      <ul className="mt-4 space-y-1.5 max-h-72 overflow-auto" data-testid="ws-receipts-list">
        {data.receipts.map((r) => (
          <li key={r.receipt_id} className="flex items-center gap-2 text-sm bg-[#F9F9F8] rounded-xl px-3 py-2">
            <span className="text-xs text-[#595D5A] w-20 shrink-0">{r.date || "—"}</span>
            <span className="flex-1 truncate text-[#1C1C1C]">{r.vendor || "Unknown"} <span className="text-[#595D5A]">· {r.category}</span></span>
            {r.status === "draft" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92600A]">unreviewed</span>}
            <span className="font-semibold text-[#1C1C1C] shrink-0">{money(r.amount)}</span>
            <button onClick={() => view(r)} className="text-[#595D5A] hover:text-[#114A31]"><Download className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
      {(data.trips || []).length > 0 && (
        <ul className="mt-3 space-y-1.5 max-h-40 overflow-auto" data-testid="ws-trips-list">
          {data.trips.map((t) => (
            <li key={t.trip_id} className="flex items-center gap-2 text-sm bg-[#F9F9F8] rounded-xl px-3 py-2">
              <Car className="h-3.5 w-3.5 text-[#114A31] shrink-0" />
              <span className="text-xs text-[#595D5A] w-20 shrink-0">{t.date}</span>
              <span className="flex-1 truncate text-[#1C1C1C]">{t.origin} → {t.destination}<span className="text-[#595D5A]"> · {t.total_miles} mi</span></span>
              <span className="font-semibold text-[#1C1C1C] shrink-0">{money(t.deduction)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default StaffReceipts;
